import { create } from "zustand";
import { z } from "zod";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateOnboardingAnnouncementBody,
  CreateOnboardingPhaseBody,
  CreateOnboardingTaskBody,
  OnboardingAnnouncement,
  OnboardingAnnouncementSchema,
  OnboardingBoardResponseSchema,
  OnboardingPhase,
  OnboardingPhaseSchema,
  OnboardingPlan,
  OnboardingPlanSchema,
  OnboardingSummaryResponse,
  OnboardingSummaryResponseSchema,
  OnboardingTask,
  OnboardingTaskSchema,
  UpdateOnboardingAnnouncementBody,
  UpdateOnboardingPhaseBody,
  UpdateOnboardingTaskBody,
  UpsertOnboardingPlanBody,
} from "@forgepoint/shared";

/** Per-task UI state for the check-off toggle. */
export interface OnboardingTaskState {
  isToggling: boolean;
  toggleError: string | null;
}

/** Per-entity UI state for settings-editor saves and deletes. */
export interface OnboardingEditState {
  isSaving: boolean;
  error: string | null;
}

interface OnboardingStore {
  plan: OnboardingPlan | null;
  phaseMap: Map<string, OnboardingPhase>;
  taskMap: Map<string, OnboardingTask>;
  announcementMap: Map<string, OnboardingAnnouncement>;
  summary: OnboardingSummaryResponse | null;
  summaryRequested: boolean;
  isLoadingBoard: boolean;
  boardLoaded: boolean;
  boardError: string | null;
  taskStateById: Map<string, OnboardingTaskState>;
  planSaveState: OnboardingEditState;
  phaseStateById: Map<string, OnboardingEditState>;
  phaseCreateState: OnboardingEditState;
  taskEditStateById: Map<string, OnboardingEditState>;
  taskCreateState: OnboardingEditState;
  announcementStateById: Map<string, OnboardingEditState>;
  announcementCreateState: OnboardingEditState;

  fetchBoard: () => Promise<void>;
  fetchSummary: () => Promise<void>;
  /** Fetches the summary once per session; later calls are no-ops. */
  ensureSummary: () => Promise<void>;
  toggleTask: (taskId: string, completed: boolean) => Promise<void>;
  /** Saves or creates the onboarding plan. Returns true on success. */
  savePlan: (body: UpsertOnboardingPlanBody) => Promise<boolean>;
  /** Adds a phase to the go-live schedule. Returns true on success. */
  createPhase: (body: CreateOnboardingPhaseBody) => Promise<boolean>;
  /** Updates a schedule phase. Returns true on success. */
  updatePhase: (
    phaseId: string,
    body: UpdateOnboardingPhaseBody,
  ) => Promise<boolean>;
  /** Deletes a schedule phase. Returns true on success. */
  deletePhase: (phaseId: string) => Promise<boolean>;
  /** Adds a task to the board. Returns true on success. */
  createTask: (body: CreateOnboardingTaskBody) => Promise<boolean>;
  /** Updates a task's fields (not check-off). Returns true on success. */
  updateTaskFields: (
    taskId: string,
    body: UpdateOnboardingTaskBody,
  ) => Promise<boolean>;
  /** Deletes a task. Returns true on success. */
  deleteTask: (taskId: string) => Promise<boolean>;
  /** Applies a batch of task order changes. Returns true on success. */
  reorderTasks: (entries: { id: string; order: number }[]) => Promise<boolean>;
  /** Posts an announcement. Returns true on success. */
  createAnnouncement: (
    body: CreateOnboardingAnnouncementBody,
  ) => Promise<boolean>;
  /** Updates an announcement. Returns true on success. */
  updateAnnouncement: (
    announcementId: string,
    body: UpdateOnboardingAnnouncementBody,
  ) => Promise<boolean>;
  /** Deletes an announcement. Returns true on success. */
  deleteAnnouncement: (announcementId: string) => Promise<boolean>;
}

/**
 * Store for the onboarding board and banner summary. Entities are kept in
 * id-keyed maps; task check-off applies optimistically and reverts on
 * failure.
 */
export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  plan: null,
  phaseMap: new Map(),
  taskMap: new Map(),
  announcementMap: new Map(),
  summary: null,
  summaryRequested: false,
  isLoadingBoard: false,
  boardLoaded: false,
  boardError: null,
  taskStateById: new Map(),
  planSaveState: { isSaving: false, error: null },
  phaseStateById: new Map(),
  phaseCreateState: { isSaving: false, error: null },
  taskEditStateById: new Map(),
  taskCreateState: { isSaving: false, error: null },
  announcementStateById: new Map(),
  announcementCreateState: { isSaving: false, error: null },

  fetchBoard: async () => {
    set({ isLoadingBoard: true, boardError: null });
    try {
      const response = await fetchApi("onboarding", "GET");
      if (!response.ok) {
        set({
          boardError: "Failed to load onboarding board",
          isLoadingBoard: false,
        });
        return;
      }

      const board = OnboardingBoardResponseSchema.parse(response.data);
      set({
        plan: board.plan,
        phaseMap: new Map(board.phases.map((phase) => [phase.id, phase])),
        taskMap: new Map(board.tasks.map((task) => [task.id, task])),
        announcementMap: new Map(
          board.announcements.map((announcement) => [
            announcement.id,
            announcement,
          ]),
        ),
        isLoadingBoard: false,
        boardLoaded: true,
      });
    } catch (error) {
      captureException(error as Error);
      set({
        boardError: "Failed to load onboarding board",
        isLoadingBoard: false,
      });
    }
  },

  fetchSummary: async () => {
    try {
      const response = await fetchApi("onboarding/summary", "GET");
      if (!response.ok) {
        return;
      }
      set({ summary: OnboardingSummaryResponseSchema.parse(response.data) });
    } catch (error) {
      captureException(error as Error);
    }
  },

  ensureSummary: async () => {
    if (get().summaryRequested) {
      return;
    }
    set({ summaryRequested: true });
    await get().fetchSummary();
  },

  savePlan: async (body: UpsertOnboardingPlanBody) => {
    set({ planSaveState: { isSaving: true, error: null } });
    try {
      const response = await fetchApi("onboarding/plan", "PUT", { body });
      if (!response.ok) {
        set({
          planSaveState: { isSaving: false, error: "Failed to save plan" },
        });
        return false;
      }
      const plan = z
        .object({ plan: OnboardingPlanSchema })
        .parse(response.data).plan;
      set({ plan, planSaveState: { isSaving: false, error: null } });
      await get().fetchSummary();
      return true;
    } catch (error) {
      captureException(error as Error);
      set({
        planSaveState: { isSaving: false, error: "Failed to save plan" },
      });
      return false;
    }
  },

  createPhase: async (body: CreateOnboardingPhaseBody) => {
    set({ phaseCreateState: { isSaving: true, error: null } });
    try {
      const response = await fetchApi("onboarding/phases", "POST", { body });
      if (!response.ok) {
        set({
          phaseCreateState: { isSaving: false, error: "Failed to add phase" },
        });
        return false;
      }
      const phase = z
        .object({ phase: OnboardingPhaseSchema })
        .parse(response.data).phase;
      set((store) => {
        const phaseMap = new Map(store.phaseMap);
        phaseMap.set(phase.id, phase);
        return { phaseMap, phaseCreateState: { isSaving: false, error: null } };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      set({
        phaseCreateState: { isSaving: false, error: "Failed to add phase" },
      });
      return false;
    }
  },

  updatePhase: async (phaseId: string, body: UpdateOnboardingPhaseBody) => {
    const setPhaseState = (state: OnboardingEditState) => {
      set((store) => {
        const phaseStateById = new Map(store.phaseStateById);
        phaseStateById.set(phaseId, state);
        return { phaseStateById };
      });
    };

    setPhaseState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(`onboarding/phases/${phaseId}`, "PUT", {
        body,
      });
      if (!response.ok) {
        setPhaseState({ isSaving: false, error: "Failed to save phase" });
        return false;
      }
      const phase = z
        .object({ phase: OnboardingPhaseSchema })
        .parse(response.data).phase;
      set((store) => {
        const phaseMap = new Map(store.phaseMap);
        phaseMap.set(phase.id, phase);
        return { phaseMap };
      });
      setPhaseState({ isSaving: false, error: null });
      return true;
    } catch (error) {
      captureException(error as Error);
      setPhaseState({ isSaving: false, error: "Failed to save phase" });
      return false;
    }
  },

  deletePhase: async (phaseId: string) => {
    const setPhaseState = (state: OnboardingEditState) => {
      set((store) => {
        const phaseStateById = new Map(store.phaseStateById);
        phaseStateById.set(phaseId, state);
        return { phaseStateById };
      });
    };

    setPhaseState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(`onboarding/phases/${phaseId}`, "DELETE");
      if (!response.ok) {
        setPhaseState({ isSaving: false, error: "Failed to delete phase" });
        return false;
      }
      set((store) => {
        const phaseMap = new Map(store.phaseMap);
        phaseMap.delete(phaseId);
        const phaseStateById = new Map(store.phaseStateById);
        phaseStateById.delete(phaseId);
        const taskMap = new Map(
          [...store.taskMap.entries()].map(([id, task]) => [
            id,
            task.phaseId === phaseId ? { ...task, phaseId: null } : task,
          ]),
        );
        return { phaseMap, phaseStateById, taskMap };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      setPhaseState({ isSaving: false, error: "Failed to delete phase" });
      return false;
    }
  },

  createTask: async (body: CreateOnboardingTaskBody) => {
    set({ taskCreateState: { isSaving: true, error: null } });
    try {
      const response = await fetchApi("onboarding/tasks", "POST", { body });
      if (!response.ok) {
        set({
          taskCreateState: { isSaving: false, error: "Failed to add task" },
        });
        return false;
      }
      const task = z
        .object({ task: OnboardingTaskSchema })
        .parse(response.data).task;
      set((store) => {
        const taskMap = new Map(store.taskMap);
        taskMap.set(task.id, task);
        return { taskMap, taskCreateState: { isSaving: false, error: null } };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      set({
        taskCreateState: { isSaving: false, error: "Failed to add task" },
      });
      return false;
    }
  },

  updateTaskFields: async (taskId: string, body: UpdateOnboardingTaskBody) => {
    const setEditState = makeEditStateSetter(set, "taskEditStateById", taskId);

    setEditState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(`onboarding/tasks/${taskId}`, "PUT", {
        body,
      });
      if (!response.ok) {
        setEditState({ isSaving: false, error: "Failed to save task" });
        return false;
      }
      const task = z
        .object({ task: OnboardingTaskSchema })
        .parse(response.data).task;
      set((store) => {
        const taskMap = new Map(store.taskMap);
        taskMap.set(task.id, task);
        return { taskMap };
      });
      setEditState({ isSaving: false, error: null });
      return true;
    } catch (error) {
      captureException(error as Error);
      setEditState({ isSaving: false, error: "Failed to save task" });
      return false;
    }
  },

  deleteTask: async (taskId: string) => {
    const setEditState = makeEditStateSetter(set, "taskEditStateById", taskId);

    setEditState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(`onboarding/tasks/${taskId}`, "DELETE");
      if (!response.ok) {
        setEditState({ isSaving: false, error: "Failed to delete task" });
        return false;
      }
      set((store) => {
        const taskMap = new Map(store.taskMap);
        taskMap.delete(taskId);
        const taskEditStateById = new Map(store.taskEditStateById);
        taskEditStateById.delete(taskId);
        return { taskMap, taskEditStateById };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      setEditState({ isSaving: false, error: "Failed to delete task" });
      return false;
    }
  },

  reorderTasks: async (entries: { id: string; order: number }[]) => {
    try {
      const response = await fetchApi("onboarding/tasks", "PUT", {
        body: { tasks: entries },
      });
      if (!response.ok) {
        return false;
      }
      set((store) => {
        const taskMap = new Map(store.taskMap);
        entries.forEach((entry) => {
          const task = taskMap.get(entry.id);
          if (task) {
            taskMap.set(entry.id, { ...task, order: entry.order });
          }
        });
        return { taskMap };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      return false;
    }
  },

  createAnnouncement: async (body: CreateOnboardingAnnouncementBody) => {
    set({ announcementCreateState: { isSaving: true, error: null } });
    try {
      const response = await fetchApi("onboarding/announcements", "POST", {
        body,
      });
      if (!response.ok) {
        set({
          announcementCreateState: {
            isSaving: false,
            error: "Failed to post announcement",
          },
        });
        return false;
      }
      const announcement = z
        .object({ announcement: OnboardingAnnouncementSchema })
        .parse(response.data).announcement;
      set((store) => {
        const announcementMap = new Map(store.announcementMap);
        announcementMap.set(announcement.id, announcement);
        return {
          announcementMap,
          announcementCreateState: { isSaving: false, error: null },
        };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      set({
        announcementCreateState: {
          isSaving: false,
          error: "Failed to post announcement",
        },
      });
      return false;
    }
  },

  updateAnnouncement: async (
    announcementId: string,
    body: UpdateOnboardingAnnouncementBody,
  ) => {
    const setEditState = makeEditStateSetter(
      set,
      "announcementStateById",
      announcementId,
    );

    setEditState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(
        `onboarding/announcements/${announcementId}`,
        "PUT",
        { body },
      );
      if (!response.ok) {
        setEditState({ isSaving: false, error: "Failed to save announcement" });
        return false;
      }
      const announcement = z
        .object({ announcement: OnboardingAnnouncementSchema })
        .parse(response.data).announcement;
      set((store) => {
        const announcementMap = new Map(store.announcementMap);
        announcementMap.set(announcement.id, announcement);
        return { announcementMap };
      });
      setEditState({ isSaving: false, error: null });
      return true;
    } catch (error) {
      captureException(error as Error);
      setEditState({ isSaving: false, error: "Failed to save announcement" });
      return false;
    }
  },

  deleteAnnouncement: async (announcementId: string) => {
    const setEditState = makeEditStateSetter(
      set,
      "announcementStateById",
      announcementId,
    );

    setEditState({ isSaving: true, error: null });
    try {
      const response = await fetchApi(
        `onboarding/announcements/${announcementId}`,
        "DELETE",
      );
      if (!response.ok) {
        setEditState({
          isSaving: false,
          error: "Failed to delete announcement",
        });
        return false;
      }
      set((store) => {
        const announcementMap = new Map(store.announcementMap);
        announcementMap.delete(announcementId);
        const announcementStateById = new Map(store.announcementStateById);
        announcementStateById.delete(announcementId);
        return { announcementMap, announcementStateById };
      });
      return true;
    } catch (error) {
      captureException(error as Error);
      setEditState({ isSaving: false, error: "Failed to delete announcement" });
      return false;
    }
  },

  toggleTask: async (taskId: string, completed: boolean) => {
    const previous = get().taskMap.get(taskId);
    if (!previous) {
      return;
    }

    const setTaskState = (state: OnboardingTaskState) => {
      set((store) => {
        const taskStateById = new Map(store.taskStateById);
        taskStateById.set(taskId, state);
        return { taskStateById };
      });
    };

    const setTask = (task: OnboardingTask) => {
      set((store) => {
        const taskMap = new Map(store.taskMap);
        taskMap.set(taskId, task);
        return { taskMap };
      });
    };

    setTaskState({ isToggling: true, toggleError: null });
    setTask({ ...previous, completed });

    try {
      const response = await fetchApi(`onboarding/tasks/${taskId}`, "PUT", {
        body: { completed },
      });
      if (!response.ok) {
        setTask(previous);
        setTaskState({
          isToggling: false,
          toggleError: "Failed to update task",
        });
        return;
      }

      const parsed = z
        .object({ task: OnboardingTaskSchema })
        .parse(response.data).task;
      setTask(parsed);
      setTaskState({ isToggling: false, toggleError: null });
      await get().fetchSummary();
    } catch (error) {
      captureException(error as Error);
      setTask(previous);
      setTaskState({ isToggling: false, toggleError: "Failed to update task" });
    }
  },
}));

type SetOnboardingState = (
  updater: (store: OnboardingStore) => Partial<OnboardingStore>,
) => void;

/**
 * Returns a setter that records one entity's edit state in the given
 * edit-state map, cloning the map so subscribers see the change.
 */
function makeEditStateSetter(
  set: SetOnboardingState,
  key: "taskEditStateById" | "announcementStateById",
  entityId: string,
): (state: OnboardingEditState) => void {
  return (state) => {
    set((store) =>
      key === "taskEditStateById"
        ? {
            taskEditStateById: new Map(store.taskEditStateById).set(
              entityId,
              state,
            ),
          }
        : {
            announcementStateById: new Map(store.announcementStateById).set(
              entityId,
              state,
            ),
          },
    );
  };
}
